import { useRateManagement } from '@api/useFreight/useFreight'
import { Form, message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import type {
  BaseFreightGroup,
  BaseFreightRate,
  BillingUnit,
  Ratebook,
} from '../../../../../../../api/useFreight/types'
import type { BaseFreightFormValues, MileageBand } from '../../../../types'

interface UseBaseFreightDrawerProps {
  ratebook: Ratebook
  visible: boolean
  onClose: () => void
  onSuccess: (created: BaseFreightGroup) => void
  editingBaseFreight: BaseFreightGroup | null
}

export function useBaseFreightDrawer({
  ratebook,
  visible,
  onClose,
  onSuccess,
  editingBaseFreight,
}: UseBaseFreightDrawerProps) {
  const [form] = Form.useForm<BaseFreightFormValues>()
  const queryClient = useQueryClient()
  const { useCreateBaseFreight, useBaseFreightQuery, useFilteredMetadataQuery, useUpdateBaseFreightDetails } =
    useRateManagement()
  const createMutation = useCreateBaseFreight()
  const updateMutation = useUpdateBaseFreightDetails()
  const { data: metadataOptions } = useFilteredMetadataQuery(ratebook)
  const { data: baseFreightRatesResponse } = useBaseFreightQuery(ratebook)

  // Get initial hasEndDate state from localStorage or from editing data
  const [hasEndDate, setHasEndDate] = useState(() => {
    if (editingBaseFreight?.rows[0]?.effective_to) {
      return true
    }
    const storedValue = localStorage.getItem('baseFreight_hasEndDate')
    return storedValue === 'true'
  })

  // Update localStorage when hasEndDate changes
  const handleHasEndDateChange = (checked: boolean) => {
    setHasEndDate(checked)
    localStorage.setItem('baseFreight_hasEndDate', checked.toString())
    if (!checked) {
      form.setFieldValue('effective_to', undefined)
    }
  }

  // Initialize form when in edit mode
  useEffect(() => {
    if (visible && editingBaseFreight) {
      const activeRate =
        editingBaseFreight.rows.find((rate: BaseFreightRate) => rate.status === 'Active') || editingBaseFreight.rows[0]

      form.setFieldsValue({
        region: editingBaseFreight.freight_region_name || undefined,
        product_group: editingBaseFreight.product_group || undefined,
        unit: activeRate.unit,
        effective_from: activeRate.effective_from ? dayjs(activeRate.effective_from) : dayjs(),
        effective_to: activeRate.effective_to ? dayjs(activeRate.effective_to) : undefined,
      })

      if (activeRate.effective_to) {
        setHasEndDate(true)
      }
    } else if (visible) {
      // Reset form for create mode
      form.resetFields()
      form.setFieldValue('effective_from', dayjs())
    }
  }, [visible, editingBaseFreight, form])

  // Generate mileage bands from 0-200 miles in 5 mile increments with realistic rates
  const generateMileageBands = (unit = 'trucks') => {
    const bands = []
    const rate = 0
    for (let i = 0; i < 200; i += 5) {
      bands.push({
        from_mile: i,
        to_mile: i + 5,
        rate,
      })
    }
    return bands
  }

  const handleSubmit = async (values: BaseFreightFormValues) => {
    try {
      const selectedRegion = metadataOptions?.freight_regions?.find(
        (r: { name: string; id: string }) => r.name === values.region
      )

      if (editingBaseFreight) {
        // create a promise for each row
        const latestRow = editingBaseFreight.rows.sort((a, b) => a.effective_from.localeCompare(b.effective_from))[0]
        const formattedData = {
          truck_rate_id: latestRow.id,
          freight_region_name: selectedRegion?.name || null,
          freight_region_id: selectedRegion?.id || null,
          product_group: values.product_group || null,
          unit: values.unit,
        }
        await updateMutation.mutateAsync(formattedData)
        queryClient.invalidateQueries({ queryKey: ['truck_rate/overview'] })
      } else {
        let ratesToUse = generateMileageBands(values.unit)

        if (values.copyFrom) {
          const [region, productGroup] = values.copyFrom.split('-')
          const existingGroup = baseFreightRatesResponse?.rows.find(
            (group) => (group.freight_region_name || '') === region && (group.product_group || '') === productGroup
          )
          if (existingGroup?.rows?.[0]) {
            ratesToUse = existingGroup.rows[0].rates as MileageBand[]
          }
        }

        const formattedData = {
          counterparty: ratebook.counterparty_name,
          counterparty_id: ratebook.counterparty_id,
          book_type: ratebook.book_type as 'Cost' | 'Revenue',
          freight_region_name: selectedRegion?.name || null,
          freight_region_id: selectedRegion?.id || null,
          product_group: values.product_group || null,
          product_group_id: null,
          unit: values.unit as BillingUnit,
          rates: ratesToUse,
          effective_from: dayjs(values.effective_from).utc().format('YYYY-MM-DD'),
          effective_to: values.effective_to ? dayjs(values.effective_to).utc().format('YYYY-MM-DD') : null,
        }

        const created = await createMutation.mutateAsync(formattedData)
        onSuccess?.(created)
      }
      queryClient.invalidateQueries({ queryKey: ['truck_rate/overview'] })
      message.success(editingBaseFreight ? 'Freight table updated successfully' : 'Freight table created successfully')

      onClose()
      form.resetFields()
    } catch (error) {
      console.error('Create/Update freight table error:', error)
      message.error(editingBaseFreight ? 'Failed to update freight table' : 'Failed to create freight table')
    }
  }

  return {
    form,
    hasEndDate,
    handleHasEndDateChange,
    handleSubmit,
    metadataOptions,
    baseFreightRatesResponse,
    isLoading: createMutation.isPending || updateMutation.isPending,
  }
}
